import React from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';

interface AgentEmptyStateProps {
  onCreateAgent: () => void;
}

const AgentEmptyState: React.FC<AgentEmptyStateProps> = ({ onCreateAgent }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: 'calc(100vh - 104px)',
        textAlign: 'center',
        bgcolor: isDarkMode ? '#1E1E1E' : '#FAFBFC',
      }}
    >
      {/* Illustration */}
      <Box
        sx={{
          mb: 4,
          '& svg': {
            width: 200,
            height: 200,
          },
        }}
      >
        <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Document with user icon */}
          <rect x="30" y="40" width="60" height="80" rx="4" fill="#E8E6FF" stroke="#3A2ADA" strokeWidth="2"/>
          <circle cx="60" cy="65" r="8" fill="#3A2ADA"/>
          <rect x="48" y="78" width="24" height="3" rx="1.5" fill="#3A2ADA"/>
          <rect x="45" y="85" width="30" height="2" rx="1" fill="#8B83DD"/>
          <rect x="45" y="90" width="30" height="2" rx="1" fill="#8B83DD"/>
          <rect x="45" y="95" width="25" height="2" rx="1" fill="#8B83DD"/>

          {/* Magnifying glass - larger */}
          <circle cx="120" cy="95" r="35" fill="white" stroke="#3A2ADA" strokeWidth="4"/>
          <circle cx="120" cy="95" r="28" fill="#F5F3FF"/>
          
          {/* Globe inside magnifying glass */}
          <circle cx="120" cy="95" r="18" fill="white" stroke="#3A2ADA" strokeWidth="2"/>
          <ellipse cx="120" cy="95" rx="18" ry="9" fill="none" stroke="#3A2ADA" strokeWidth="1.5"/>
          <line x1="120" y1="77" x2="120" y2="113" stroke="#3A2ADA" strokeWidth="1.5"/>
          <line x1="102" y1="95" x2="138" y2="95" stroke="#3A2ADA" strokeWidth="1.5"/>
          
          {/* Magnifying glass handle */}
          <rect x="145" y="120" width="30" height="8" rx="4" fill="#3A2ADA" transform="rotate(45 145 120)"/>

          {/* Document with chart */}
          <rect x="110" y="130" width="60" height="50" rx="4" fill="#E8E6FF" stroke="#3A2ADA" strokeWidth="2"/>
          {/* Bar chart */}
          <rect x="120" y="155" width="8" height="15" rx="1" fill="#3A2ADA"/>
          <rect x="132" y="150" width="8" height="20" rx="1" fill="#3A2ADA"/>
          <rect x="144" y="145" width="8" height="25" rx="1" fill="#3A2ADA"/>
          <rect x="120" y="142" width="32" height="2" rx="1" fill="#8B83DD"/>

          {/* Lines document */}
          <rect x="125" y="35" width="50" height="60" rx="4" fill="#E8E6FF" stroke="#3A2ADA" strokeWidth="2"/>
          <rect x="132" y="45" width="36" height="3" rx="1.5" fill="#3A2ADA"/>
          <rect x="132" y="52" width="30" height="2" rx="1" fill="#8B83DD"/>
          <rect x="132" y="57" width="32" height="2" rx="1" fill="#8B83DD"/>
          <rect x="132" y="62" width="28" height="2" rx="1" fill="#8B83DD"/>
          <rect x="132" y="67" width="30" height="2" rx="1" fill="#8B83DD"/>
          <rect x="132" y="72" width="25" height="2" rx="1" fill="#8B83DD"/>
          <rect x="132" y="77" width="32" height="2" rx="1" fill="#8B83DD"/>
          <rect x="132" y="82" width="27" height="2" rx="1" fill="#8B83DD"/>
        </svg>
      </Box>

      {/* Text */}
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          fontSize: '20px',
          color: isDarkMode ? '#FFFFFF' : '#1F2937',
          mb: 1,
        }}
      >
        There is no data available,
      </Typography>

      <Typography
        variant="body1"
        sx={{
          fontSize: '16px',
          color: isDarkMode ? '#9CA3AF' : '#6B7280',
          mb: 3,
          maxWidth: 400,
        }}
      >
        You have zero Agents. Configure an Agent by clicking on 'Create Agent'
      </Typography>

      {/* Create Agent Button */}
      <Button
        variant="contained"
        onClick={onCreateAgent}
        sx={{
          bgcolor: '#3A2ADA',
          color: '#FFFFFF',
          textTransform: 'none',
          fontSize: '16px',
          fontWeight: 600,
          px: 4,
          py: 1.5,
          borderRadius: '8px',
          '&:hover': {
            bgcolor: '#2F1FBD',
          },
        }}
      >
        Create Agent
      </Button>
    </Box>
  );
};

export default AgentEmptyState;
